/**
 * Brand primitives — one source of truth for the NeetOne logo and the
 * "powered by Admission Hall Advisors" tagline. Use these instead of typing "NeetOne"
 * anywhere so a future rebrand only touches one file.
 *
 * All assets live under /app/frontend/public and are pre-generated at multiple
 * sizes so the browser can pick the closest source (crisp on retina, small on
 * hi-dpi sidebars).
 */

const MARK_SRC = "/brand-mark-256.png";
const MARK_SRCSET = "/brand-mark-96.png 96w, /brand-mark-256.png 256w, /brand-mark-512.png 512w";
const LOCKUP_SRC = "/brand-lockup-512.png";
const LOCKUP_SRCSET = "/brand-lockup-256.png 256w, /brand-lockup-512.png 512w, /brand-lockup-1024.png 1024w";

/**
 * Just the N/1 mark. Sizes: xs 16px, sm 20px, md 28px, lg 40px, xl 56px.
 * Alt is decorative when a wordmark sits alongside; screen readers still get
 * the label via aria-label on the parent link.
 */
export const BrandMark = ({ size = "md", className = "", alt = "" }) => {
  const px = { xs: 16, sm: 20, md: 28, lg: 40, xl: 56, "2xl": 72 }[size] ?? 28;
  return (
    <img
      src={MARK_SRC}
      srcSet={MARK_SRCSET}
      sizes={`${px}px`}
      width={px}
      height={px}
      alt={alt}
      className={`inline-block shrink-0 ${className}`}
      draggable={false}
    />
  );
};

/**
 * Mark + "NeetOne" wordmark, laid out inline. `wordmark="hide"` collapses to
 * just the mark on narrow viewports.
 */
export const BrandLockup = ({ size = "md", tone = "dark", className = "", wordmark = "auto" }) => {
  const wordCls =
    size === "sm"
      ? "text-base"
      : size === "lg"
        ? "text-2xl"
        : size === "xl"
          ? "text-3xl"
          : "text-lg";
  const wordColor = tone === "light" ? "text-white" : "text-[#1A365D]";
  const hideCls = wordmark === "hide" ? "hidden sm:inline" : wordmark === "always" ? "" : "hidden sm:inline";
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <BrandMark size={size} />
      <span
        className={`font-display font-extrabold tracking-tighter ${wordCls} ${wordColor} ${hideCls}`}
      >
        NeetOne
      </span>
    </span>
  );
};

/** The full lockup as a single artwork — used on splash / hero blocks. */
export const BrandArtwork = ({ width = 220, className = "" }) => (
  <img
    src={LOCKUP_SRC}
    srcSet={LOCKUP_SRCSET}
    sizes={`${width}px`}
    width={width}
    alt="NeetOne"
    className={`inline-block ${className}`}
    draggable={false}
  />
);

/** Tagline that appears under the brand in navs, footers, and auth splashes. */
export const PoweredByAdmissionHall = ({ tone = "muted", className = "" }) => {
  const color =
    tone === "light" ? "text-blue-200" : tone === "on-dark" ? "text-white/70" : "text-slate-500";
  return (
    <span
      data-testid="powered-by-admission-hall"
      className={`whitespace-nowrap text-[10px] font-semibold uppercase tracking-widest ${color} ${className}`}
    >
      Powered by Admission Hall Advisors
    </span>
  );
};
